import React, { FC, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as DeleteIcon } from "../../../../../../assets/images/delete-icon.svg";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  TransferSearchParticipantResult,
  TransferSearchParticipantResultFormKey,
  transferSteps,
} from "../../../../modelsAndSchemas/Models";

import {
  FormController,
  MuiBox,
  MuiCheckbox,
  MuiIconButton,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  RowProps,
} from "../../../../../../components";
import { MuiSelectedList } from "../../../../../../components/selectedList/MuiSelectedList";
import { MAX_LENGTH_75 } from "../../../../../../helpers/constants";
import { LabelValue } from "../../../../../../helpers/types/common";
import {
  checkFilterMatch,
  getFullName,
  getValueOrBlank,
  getValueOrZero,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import {
  bulkTransferSearchInputClickSelector,
  bulkTransferSelectedParticipantsSelector,
} from "../../../../../../redux/slices/program/programSelector";
import { setBulkTransferSelectedParticipants } from "../../../../../../redux/slices/program/programSlice";
import { bulkTransferSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { SearchParticipantsResultWrapper } from "./SearchParticipantsResult.style";

interface SearchParticipantsResultProps {
  rows: TransferSearchParticipantResult[];
  pageId: string;
  setShowInformationModal?: (value: boolean) => void;
  currentStep?: number;
  handleDeleteClick?: (id: string) => void;
}

export const SearchParticipantsResult: FC<SearchParticipantsResultProps> = ({
  rows,
  pageId,
  setShowInformationModal,
  currentStep = 0,
  handleDeleteClick,
}) => {
  const dispatch = useDispatch();
  const isTransferDetailStep = currentStep === transferSteps.transferDetails;

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const selectedParticipants = useSelector(
    bulkTransferSelectedParticipantsSelector,
  );

  const isSearchInputClick = useSelector(bulkTransferSearchInputClickSelector);
  const { fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);

  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [filteredResults, setFilteredResults] = useState([...rows]);

  const [allUpdatedRows, setAllUpdatedRows] = useState<
    TransferSearchParticipantResult[]
  >([]);
  const [uniqueLineOfBusinesses, setUniqueLineOfBusinesses] = useState<
    LabelValue[]
  >([]);
  const [uniqueTemplates, setUniqueTemplates] = useState<LabelValue[]>([]);
  const [uniqueDeliveryCountries, setUniqueDeliveryCountries] = useState<
    LabelValue[]
  >([]);

  const getResultLabel = (key: string) => {
    return fetchLabel(
      bulkTransferSiteCoreKeys.searchParticipantsResultsKey,
      key,
    );
  };

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const defaultSearchParticipantResult = {
    [TransferSearchParticipantResultFormKey.participantName]: "",
    [TransferSearchParticipantResultFormKey.programName]: "",
    [TransferSearchParticipantResultFormKey.programTemplateName]: "",
    [TransferSearchParticipantResultFormKey.deliveryCountryName]: "",
    [TransferSearchParticipantResultFormKey.lobName]: "",
    [TransferSearchParticipantResultFormKey.solutionOfferingsName]: "",
    [TransferSearchParticipantResultFormKey.clientName]: "",
  };

  const methods = useForm({
    mode: "all",
    defaultValues: defaultSearchParticipantResult,
  });

  const { reset, control, getValues } = methods;

  const filters = useWatch({ control });

  useEffect(() => {
    resetPagination();
    const myData = structuredClone(rows);

    const uniqueCountries = Array.from(
      new Set(myData.map((item) => item.deliveryCountryName)),
    ).map((country) => ({ value: country, label: country }));

    const uniqueTemplate = Array.from(
      new Set(myData.map((item) => item.programTemplateName)),
    ).map((label) => ({
      value: label,
      label: label,
    }));

    const uniqueLineOfBusiness = Array.from(
      new Set(myData.map((item) => item.lobName)),
    ).map((label) => ({
      value: label,
      label: label,
    }));

    setUniqueLineOfBusinesses(uniqueLineOfBusiness);
    setUniqueDeliveryCountries(uniqueCountries);
    setUniqueTemplates(uniqueTemplate);
    setIsAllSelected(false);

    reset();
  }, [rows]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = structuredClone(rows);

      localData.forEach((data: TransferSearchParticipantResult) => {
        data.isSelected = false;
        data.participantName = getFullName(
          data.participantFirstName,
          data.participantLastName,
          true,
        );
      });

      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);
    }
  }, [rows, siteCoreLoading]);

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        participantName,
        lobName,
        programTemplateName,
        deliveryCountryName,
        solutionOfferingsName,
        clientName,
      } = getValues();

      const myData = allUpdatedRows.filter(
        (item: TransferSearchParticipantResult) => {
          const participantNameMatch = checkFilterMatch(
            participantName,
            getValueOrBlank(item.participantName),
          );

          const programTemplateMatch = checkFilterMatch(
            programTemplateName,
            item.programTemplateName,
          );

          const lineOfBusinessMatch = checkFilterMatch(lobName, item.lobName);

          const nextViewMatch = checkFilterMatch(
            solutionOfferingsName,
            item.solutionOfferingsName,
          );

          const deliveryCountryMatch = checkFilterMatch(
            deliveryCountryName,
            item.deliveryCountryName,
          );

          const clientNameMatch = checkFilterMatch(clientName, item.clientName);

          return (
            participantNameMatch &&
            lineOfBusinessMatch &&
            programTemplateMatch &&
            deliveryCountryMatch &&
            nextViewMatch &&
            clientNameMatch
          );
        },
      );
      setFilteredResults([...myData]);
      resetPagination();
      setAllCheckboxSelected(myData);
    }
  }, [filters]);

  const storeSelectedDataInRedux = (
    filteredResults: TransferSearchParticipantResult[],
  ) => {
    dispatch(setBulkTransferSelectedParticipants(filteredResults));
  };

  const setAllCheckboxSelected = (
    updatedRows: TransferSearchParticipantResult[],
  ) => {
    // Check if all rows have isSelected as true
    const allSelected = updatedRows.every(
      (row: TransferSearchParticipantResult) => row.isSelected === true,
    );
    setIsAllSelected(updatedRows.length ? allSelected : false);
  };

  const handleRowCheckboxChange = (checked: boolean, rowId: string) => {
    // Update only the clicked row's `isSelected` value
    if (isSearchInputClick) {
      const updatedRows = filteredResults.map(
        (row: TransferSearchParticipantResult) =>
          row.participantId === rowId ? { ...row, isSelected: checked } : row,
      );

      setAllCheckboxSelected(updatedRows);
      setFilteredResults([...updatedRows]);

      const updatedAllRows = allUpdatedRows.map((row) =>
        row.participantId === rowId ? { ...row, isSelected: checked } : row,
      );

      storeSelectedDataInRedux([...updatedAllRows]);
      setAllUpdatedRows([...updatedAllRows]);
    } else {
      setShowInformationModal?.(true);
    }
  };

  const onHeaderCheckBoxChange = (checked: boolean) => {
    if (isSearchInputClick) {
      setIsAllSelected(checked);
      const updatedRows = filteredResults.map(
        (row: TransferSearchParticipantResult) => ({
          ...row,
          isSelected: checked,
        }),
      );

      setFilteredResults([...updatedRows]);

      // Also update `allUpdatedRows` to reflect changes for the whole dataset
      const updatedAllRows = allUpdatedRows.map(
        (row: TransferSearchParticipantResult) => {
          const matchingRow = updatedRows.find(
            (updatedRow) => updatedRow.participantId === row.participantId,
          );
          return matchingRow
            ? { ...row, isSelected: matchingRow.isSelected }
            : row;
        },
      );

      // Update `allUpdatedRows` state
      setAllUpdatedRows([...updatedAllRows]);
      storeSelectedDataInRedux([...updatedAllRows]);
    } else {
      setShowInformationModal?.(true);
    }
  };

  //to reset deleted participants as uncheck if user came from second tab
  useEffect(() => {
    if (!selectedParticipants.length) return;
    const updatedFilteredResults = filteredResults.map((row) => {
      const match = selectedParticipants.find(
        (p) => p.participantId === row.participantId,
      );
      return match ? { ...row, isSelected: match.isSelected } : row;
    });
    const updatedRows = allUpdatedRows.map((row) => {
      const match = selectedParticipants.find(
        (p) => p.participantId === row.participantId,
      );
      return match ? { ...row, isSelected: match.isSelected } : row;
    });
    setFilteredResults(updatedFilteredResults);
    setAllCheckboxSelected(updatedFilteredResults);
    setAllUpdatedRows(updatedRows);
  }, [currentStep]);

  const headerRender = () => {
    return (
      <MuiCheckbox
        checked={isAllSelected}
        checkboxLabel={`${commonARIALabel?.selectAll}`}
        hideLabel={true}
        onCheckedChange={(checked: boolean) => onHeaderCheckBoxChange(checked)}
      />
    );
  };

  const columns = [
    ...(!isTransferDetailStep
      ? [
          {
            id: TransferSearchParticipantResultFormKey.isSelected,
            label: "",
            className: "checkbox-cell",
            isSortVisible: false,
            headerRender: headerRender,
            render: (row: RowProps) => {
              return (
                <MuiCheckbox
                  checked={row.isSelected}
                  checkboxLabel={`${row.name}`}
                  hideLabel={true}
                  id={`${pageId}-${row.participantId}`}
                  onCheckedChange={(checked: boolean) => {
                    handleRowCheckboxChange(checked, row.participantId);
                  }}
                  key={`${pageId}-${row.participantId}-${row.isSelected}`}
                />
              );
            },
          },
        ]
      : []),
    {
      id: TransferSearchParticipantResultFormKey.participantName,
      label: getResultLabel(bulkTransferSiteCoreKeys.participantName),
      isEmptyValue: true,
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography variant="h3" className="cell">
          {row.participantName}
        </MuiTypography>
      ),
    },
    {
      id: TransferSearchParticipantResultFormKey.deliveryCountryName,
      label: getResultLabel(bulkTransferSiteCoreKeys.resultDeliveryCountry),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: TransferSearchParticipantResultFormKey.lobName,
      label: getResultLabel(bulkTransferSiteCoreKeys.resultProductLine),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: TransferSearchParticipantResultFormKey.programTemplateName,
      label: getResultLabel(bulkTransferSiteCoreKeys.resultProgramTemplate),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: TransferSearchParticipantResultFormKey.clientName,
      label: getResultLabel(bulkTransferSiteCoreKeys.resultClient),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: TransferSearchParticipantResultFormKey.solutionOfferingsName,
      label: getResultLabel(bulkTransferSiteCoreKeys.resultNextView),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    ...(handleDeleteClick
      ? [
          {
            id: "action",
            label: getResultLabel(bulkTransferSiteCoreKeys.action),
            className: "action",
            render: (row: RowProps) => {
              return (
                <MuiIconButton
                  onClick={() => handleDeleteClick?.(row.participantId)}
                  data-testid={`${pageId}-delete-participant`}
                  aria-label={commonARIALabel.delete}
                >
                  <DeleteIcon />
                </MuiIconButton>
              );
            },
          },
        ]
      : []),
  ];

  const tableHeaderForm = [
    ...(!isTransferDetailStep ? [<></>] : []),
    <FormController
      control={control}
      key={TransferSearchParticipantResultFormKey.participantName}
      name={TransferSearchParticipantResultFormKey.participantName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${TransferSearchParticipantResultFormKey.participantName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={TransferSearchParticipantResultFormKey.deliveryCountryName}
      name={TransferSearchParticipantResultFormKey.deliveryCountryName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueDeliveryCountries}
        value=""
        id={`${pageId}-${TransferSearchParticipantResultFormKey.deliveryCountryName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={TransferSearchParticipantResultFormKey.lobName}
      name={TransferSearchParticipantResultFormKey.lobName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueLineOfBusinesses}
        value=""
        id={`${pageId}-${TransferSearchParticipantResultFormKey.lobName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <FormController
      control={control}
      name={TransferSearchParticipantResultFormKey.programTemplateName}
      key={TransferSearchParticipantResultFormKey.programTemplateName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueTemplates}
        value=""
        id={`${pageId}-${TransferSearchParticipantResultFormKey.programTemplateName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <FormController
      control={control}
      key={TransferSearchParticipantResultFormKey.clientName}
      name={TransferSearchParticipantResultFormKey.clientName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${TransferSearchParticipantResultFormKey.clientName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={TransferSearchParticipantResultFormKey.solutionOfferingsName}
      name={TransferSearchParticipantResultFormKey.solutionOfferingsName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${TransferSearchParticipantResultFormKey.solutionOfferingsName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    ...(handleDeleteClick ? [<></>] : []),
  ];

  const handleRemove = (id: number | string) => {
    handleRowCheckboxChange(false, id as string);
  };

  const selectedItems = useMemo(() => {
    return selectedParticipants
      .filter((participant) => participant.isSelected)
      .map((item) => ({
        label: item.participantName ?? "",
        value: item.participantId,
      }));
  }, [selectedParticipants]);

  return (
    <SearchParticipantsResultWrapper>
      {!isTransferDetailStep && (
        <MuiBox className="search-result-header-wrapper">
          <MuiTypography
            variant="h2SemiBold"
            className="search-result-header"
            component="div"
          >
            {getResultLabel(bulkTransferSiteCoreKeys.chooseParticipants)}
          </MuiTypography>
          <MuiBox className="search-header-right">
            <MuiSelectedList
              selectedItems={selectedItems}
              onRemove={handleRemove}
              id="participants-select"
              label={replaceHashWithValue(
                getResultLabel(
                  bulkTransferSiteCoreKeys.participantsSelectedDropDownText,
                ),
                String(getValueOrZero(selectedItems?.length)),
              )}
              requiredARIALabel={commonARIALabel.required}
            />
          </MuiBox>
        </MuiBox>
      )}

      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        tableResults={[...filteredResults]}
        noDataInApi={filteredResults.length === 0}
        noResultMessage={commonLabels.noResult}
        priorityResults={[]}
        emptyMessageColSpan={8}
        rowIdKey="participantId"
        isLastColumnSticky={!!handleDeleteClick}
      />
    </SearchParticipantsResultWrapper>
  );
};
